import {
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  DeleteRegular,
  DismissRegular,
  EditFilled,
  EditRegular,
  PersonAddRegular,
  ShieldRegular,
} from "@fluentui/react-icons";
import { Fragment, useEffect, useMemo, useState } from "react";
import { RoleDTO } from "../../models/role";
import { UpdateUserGroup, UserGroupEmail } from "../../models/userGroup";
import { useAppContext } from "../../AppContext";
import { updateUserGroup } from "../../services/userGroupService";
import { getOrganizations } from "../../services/organizationService";
import { Organization } from "../../models/organizations";
import { convertToLocaleDateString } from "../../utils/date";
import InviteUserToGroupDialog from "../dialogs/InviteUserToGroupDialog";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import RenameUserGroupDialog from "../dialogs/RenameUserGroupDialog";
import UserDrawer from "./UserDrawer";
import AddRolesDialog from "../dialogs/AddRolesDialog";

type UserGroupAssignment = {
  user: {
    userID: number;
    email: string;
    firstName: string;
    lastName: string;
    lastActivity?: string;
    created?: string;
  };
  isAdmin: boolean;
  created?: string;
  roles?: { roleID: number; roleName: string }[];
};

type UserGroup = {
  userGroupID: number;
  userGroupName: string;
  moduleID?: number;
  userGroupAssignments: UserGroupAssignment[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  userGroup: UserGroup | null;
  onGroupUpdated?: () => void;
};

const UserGroupMembersDrawer = (props: Props) => {
  const { user, featurePermissions } = useAppContext();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    const fetchOrganizations = async () => {
      try {
        const orgs = await getOrganizations();
        setOrganizations(orgs.sort((a: Organization, b: Organization) => a.organizationName.localeCompare(b.organizationName)));
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, [props.open]);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [removingUserID, setRemovingUserID] = useState<number | null>(null);
  const [pendingDeleteUserID, setPendingDeleteUserID] = useState<number | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [togglingUserID, setTogglingUserID] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [hoveredUserID, setHoveredUserID] = useState<number | null>(null);
  const [roleEditUser, setRoleEditUser] = useState<any | null>(null);
  const [isAddRolesDialogOpen, setIsAddRolesDialogOpen] = useState(false);

  // Behörighetsansvarig users first, then Kommunanvändare, each alphabetically by email
  const sortedAssignments = useMemo(() => {
    if (!props.userGroup) return [];
    return [...props.userGroup.userGroupAssignments].sort((a, b) => {
      const aIsBehorighetsansvarig = a.roles?.some((r) => r.roleName === "Behörighetsansvarig") ?? false;
      const bIsBehorighetsansvarig = b.roles?.some((r) => r.roleName === "Behörighetsansvarig") ?? false;
      if (aIsBehorighetsansvarig !== bIsBehorighetsansvarig) {
        return aIsBehorighetsansvarig ? -1 : 1;
      }
      return a.user.email.localeCompare(b.user.email, "sv");
    });
  }, [props.userGroup]);

  if (!props.userGroup) return null;

  const canManageGroup = (): boolean => {
    if (!user) return false;
    const isGroupAdmin = user.userGroupAssignments
      ?.some((uga: any) => uga.userGroup.userGroupID === props.userGroup!.userGroupID
        && uga.userGroup.userGroupAssignments?.some((member: any) =>
          member.user?.userID === user.user.userID && member.roles?.some((r: any) => r.roleName === "Behörighetsansvarig")
        ));
    const isRamavtalsansvarig = user.assignments
      ?.filter((a: any) => a.role.roleName === "Ramavtalsansvarig" || a.role.roleName === "Kontoadmin")
      .some((a: any) => a.module.moduleID === props.userGroup!.moduleID);
    const isGeneral = user.roles?.some(
      (r: RoleDTO) => r.roleType === "General"
    );
    return isGroupAdmin || isRamavtalsansvarig || isGeneral;
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteUserID === null) return;
    setIsConfirmDeleteOpen(false);
    await handleRemoveUser(pendingDeleteUserID);
    setPendingDeleteUserID(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmDeleteOpen(false);
    setPendingDeleteUserID(null);
  };

  const handleRemoveUser = async (userID: number) => {
    if (!props.userGroup) return;
    const remaining = props.userGroup.userGroupAssignments.filter(
      (a) => a.user.userID !== userID
    );
    const emails: UserGroupEmail[] = remaining.map((a) => ({
      Email: a.user.email,
      IsAdmin: a.isAdmin,
    }));
    const submitData: UpdateUserGroup = {
      UserGroupID: props.userGroup.userGroupID,
      Emails: emails,
    };
    setRemovingUserID(userID);
    try {
      await updateUserGroup(submitData);
      props.onGroupUpdated?.();
    } catch (err) {
      console.error("Error removing user from group:", err);
    } finally {
      setRemovingUserID(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "–";
    try {
      return convertToLocaleDateString(dateStr);
    } catch {
      return "–";
    }
  };

  const existingEmails: UserGroupEmail[] =
    props.userGroup.userGroupAssignments.map((a) => ({
      Email: a.user.email,
      IsAdmin: a.isAdmin,
    }));

  return (
    <>
      <Drawer
        position="end"
        open={props.open}
        onOpenChange={(_e, data) => !data.open && props.onClose()}
        style={{ width: "950px" }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle>
            <div className="flex items-start justify-between w-full">
              <span className="break-all">{props.userGroup.userGroupName}</span>
              <Button
                size="small"
                shape="circular"
                icon={<DismissRegular />}
                onClick={props.onClose}
              />
            </div>
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <div className="flex flex-col gap-4 pt-4">
            <div className="flex gap-2 flex-wrap">
              {canManageGroup() && (
                <Button
                  icon={<PersonAddRegular />}
                  onClick={() => setIsInviteDialogOpen(true)}
                >
                  Lägg till medarbetare
                </Button>
              )}
              {canManageGroup() && (
                <Button
                  icon={<EditRegular />}
                  onClick={() => setIsRenameDialogOpen(true)}
                >
                  Ändra namn på grupp
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>
                      <Text weight="semibold">E-post/Namn</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text weight="semibold" size={200}>Senaste inloggning</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text weight="semibold" size={200}>Lades till i grupp</Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text weight="semibold" size={200}>Roller</Text>
                    </TableHeaderCell>
                    <TableHeaderCell style={{ width: 150 }} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAssignments.length > 0 ? (
                    sortedAssignments.map((assignment) => {
                      const isHovered = hoveredUserID === assignment.user.userID;
                      const rowBg = isHovered
                        ? { backgroundColor: "var(--colorNeutralBackground1Hover)" }
                        : {};
                      return (
                        <Fragment key={assignment.user.userID}>
                          <TableRow
                            className="linked-row-top"
                            onMouseEnter={() => setHoveredUserID(assignment.user.userID)}
                            onMouseLeave={() => setHoveredUserID(null)}
                            style={{ ...rowBg, borderBottom: "none" }}
                          >
                            <TableCell colSpan={3}>
                              <Text className="break-all">{assignment.user.email}</Text>
                            </TableCell>
                            <TableCell rowSpan={2} style={{ verticalAlign: "middle" }}>
                              <div className="flex flex-col gap-1">
                                {assignment.roles?.some(r => r.roleName === "Kommunanvändare") && (
                                  <Badge
                                    appearance="filled"
                                    style={{ backgroundColor: "#4FA0E8", color: "#ffffff" }}
                                  >
                                    Kommunanvändare
                                  </Badge>
                                )}
                                {assignment.roles?.some(r => r.roleName === "Behörighetsansvarig") && (
                                  <Badge color="brand">Behörighetsansvarig</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell rowSpan={2} style={{ width: 112, verticalAlign: "middle" }}>
                              <div className="flex gap-1 justify-end">
                                {canManageGroup() && (
                                  <Tooltip content="Hantera roller i grupp" relationship="label">
                                    <Button
                                      size="small"
                                      appearance="subtle"
                                      shape="circular"
                                      icon={<ShieldRegular />}
                                      onClick={() => {
                                        setRoleEditUser(assignment.user);
                                        setIsAddRolesDialogOpen(true);
                                      }}
                                    />
                                  </Tooltip>
                                )}                              
                                {canManageGroup() && (
                                  <Tooltip content="Ta bort" relationship="label">
                                    <Button
                                      size="small"
                                      appearance="subtle"
                                      icon={
                                        removingUserID === assignment.user.userID ? (
                                          <Spinner size="tiny" />
                                        ) : (
                                          <DeleteRegular />
                                        )
                                      }
                                      disabled={removingUserID === assignment.user.userID}
                                      onClick={() => {
                                        setPendingDeleteUserID(assignment.user.userID);
                                        setIsConfirmDeleteOpen(true);
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {featurePermissions?.some(p => p.action == 'Open user') && (
                                  <Tooltip content="Användaruppgifter" relationship="label">
                                    <Button
                                      size="small"
                                      appearance="subtle"
                                      shape="circular"
                                      icon={<EditRegular />}
                                      onClick={() => {
                                        setSelectedUser(assignment.user);
                                        setIsUserDrawerOpen(true);
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            onMouseEnter={() => setHoveredUserID(assignment.user.userID)}
                            onMouseLeave={() => setHoveredUserID(null)}
                            style={rowBg}
                          >
                            <TableCell>
                              <Text size={200} className="break-all">
                                {assignment.user.firstName} {assignment.user.lastName}
                              </Text>
                            </TableCell>
                            <TableCell>
                              <Text size={200}>{formatDate(assignment.user.lastActivity)}</Text>
                            </TableCell>
                            <TableCell>
                              <Text size={200}>{formatDate(assignment.created)}</Text>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className="flex justify-center py-8">
                          <Text>Inga medarbetare i gruppen</Text>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DrawerBody>
      </Drawer>
      <ConfirmationDialog
        isOpen={isConfirmDeleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Ta bort medarbetare"
        text={`Är du säker på att du vill ta bort ${
          props.userGroup.userGroupAssignments.find(
            (a) => a.user.userID === pendingDeleteUserID
          )?.user.email ?? "användaren"
        } från gruppen?`}
      />
      <InviteUserToGroupDialog
        open={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        userGroup={props.userGroup}
        onUserInvited={props.onGroupUpdated}
      />
      <RenameUserGroupDialog
        open={isRenameDialogOpen}
        onClose={() => setIsRenameDialogOpen(false)}
        userGroupID={props.userGroup.userGroupID}
        currentName={props.userGroup.userGroupName}
        existingEmails={existingEmails}
        onRenamed={props.onGroupUpdated}
      />
      {selectedUser && (
        <UserDrawer
          open={isUserDrawerOpen}
          onClose={() => {
            setIsUserDrawerOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          organizations={organizations}
          onUserUpdated={() => {}}
        />
      )}
      {roleEditUser && (
        <AddRolesDialog
          open={isAddRolesDialogOpen}
          onClose={() => {
            setIsAddRolesDialogOpen(false);
            setRoleEditUser(null);
          }}
          organizations={organizations}
          user={roleEditUser}
          onRoleAssigned={() => props.onGroupUpdated?.()}
          presetUserGroup={props.userGroup}
        />
      )}
    </>
  );
};

export default UserGroupMembersDrawer;
